import { useMemo } from "react";
import { PaginationProps } from "./types";
import { generatePagesNumbers } from "./utils";
import { PaginationButton } from "./pagination-button";
import styles from "./styles.module.scss";

export const Pagination = ({
  totalPages,
  currentPage,
  dataTestId,
  onPageChange,
}: PaginationProps) => {
  const { head, tail } = useMemo(
    () => generatePagesNumbers(currentPage, totalPages),
    [totalPages, currentPage]
  );

  const isPrevPageButtonDisabled = currentPage <= 1;
  const isNextPageButtonDisabled = currentPage >= totalPages;

  const handlePageChange = (page: number) => () => onPageChange(page);

  return (
    <nav className={styles.nav} data-test-id={dataTestId}>
      <PaginationButton
        type="double-arrow-left"
        disabled={isPrevPageButtonDisabled}
        onClick={handlePageChange(1)}
        dataTestId={`${dataTestId}-first-page-button`}
      />

      <PaginationButton
        type="arrow-left"
        disabled={isPrevPageButtonDisabled}
        onClick={handlePageChange(currentPage - 1)}
        dataTestId={`${dataTestId}-prev-page-button`}
      />

      {head.map((headPageNumber) => (
        <PaginationButton
          text={headPageNumber}
          key={headPageNumber}
          onClick={handlePageChange(headPageNumber)}
          dataTestId={`${dataTestId}-${headPageNumber}-page-button`}
        />
      ))}

      <PaginationButton
        active
        text={currentPage}
        key={currentPage}
        dataTestId={`${dataTestId}-active-page-button`}
      />

      {tail.map((tailPageNumber) => (
        <PaginationButton
          text={tailPageNumber}
          key={tailPageNumber}
          onClick={handlePageChange(tailPageNumber)}
          dataTestId={`${dataTestId}-${tailPageNumber}-page-button`}
        />
      ))}

      <PaginationButton
        type="arrow-right"
        disabled={isNextPageButtonDisabled}
        onClick={handlePageChange(currentPage + 1)}
        dataTestId={`${dataTestId}-next-page-button`}
      />

      <PaginationButton
        type="double-arrow-right"
        disabled={isNextPageButtonDisabled}
        onClick={handlePageChange(totalPages)}
        dataTestId={`${dataTestId}-last-page-button`}
      />
    </nav>
  );
};
